import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog/Dialog";
import { useEnvironment } from "@/hooks/useEnvironment";
import { useEffect, useRef, useState } from "react";

const PRODUCTION_MESSAGE =
  "You have a large number of patient charts currently open that could affect the stability of your session. Please close out any charts that you are not currently reviewing or charting on";

const DEV_MESSAGES = [
  "You currently have enough charts open to qualify as a stress test. If you'd like this session to remain alive, consider closing the tabs you're no longer pretending to review.",
  "You have an impressive number of charts open. Unfortunately, your browser interprets this as an act of violence. Please close any charts you are not actively using before the session collapses under the weight of your ambition.",
  "You currently have far too many charts open. We admire the optimism that you'll get back to all of them, but your session disagrees. Please close unused charts before everything catches fire.",
  "Your tab management strategy has become our infrastructure problem. Please close out any charts that you are not currently reviewing or charting on.",
  "Congratulations, you've discovered the upper limit of 'just one more tab.' Please close out any charts that you are not currently reviewing or charting on.",
  "This session is being held together by hope and browser garbage collection. Please close out any charts that you are not currently reviewing or charting on.",
];

const pickDevMessage = () =>
  DEV_MESSAGES[Math.floor(Math.random() * DEV_MESSAGES.length)];

interface TooManyTabsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TooManyTabsModal = ({
  isOpen,
  onOpenChange,
}: TooManyTabsModalProps) => {
  const { isDev, isLocal } = useEnvironment();
  const useDevMessages = isDev || isLocal;

  const [message, setMessage] = useState(() =>
    useDevMessages ? pickDevMessage() : PRODUCTION_MESSAGE,
  );
  const wasOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current && useDevMessages) {
      setMessage(pickDevMessage());
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, useDevMessages]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Too many charts open</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
